import { memo, ReactNode, useEffect } from "react";

export const TextHighlighterProvider = memo(function TextHighlighterProvider({
  children,
}: {
  children: ReactNode;
}) {
  useEffect(() => {
    const checkAndHighlight = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const highlightText = urlParams.get("highlightText");

      if (!highlightText) {
        return;
      }

      // Wait for DOM to fully render
      setTimeout(() => {
        try {
          const sanitizedText =
            highlightText.length > 100
              ? highlightText.substring(0, 100)
              : highlightText;
          // Escape special regex characters to treat as literal text
          const escapedText = sanitizedText.replace(
            /[.*+?^${}()|[\]\\]/g,
            "\\$&",
          );
          const regex = new RegExp(escapedText, "gi");
          const found = searchAndHighlightText(regex);

          if (found) {
            // Remove the query param after highlighting
            urlParams.delete("highlightText");
            const newUrl = urlParams.toString()
              ? `${window.location.pathname}?${urlParams.toString()}`
              : window.location.pathname;
            window.history.replaceState({}, "", newUrl);
          } else {
            console.warn(
              `TextHighlighterProvider: No matches found for pattern "${highlightText}"`,
            );
          }
        } catch (error) {
          console.error("Error highlighting text:", error);
        }
      }, 1000);
    };

    // Check on mount
    checkAndHighlight();

    // Intercept pushState and replaceState to detect navigation
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function (...args) {
      originalPushState.apply(window.history, args);
      checkAndHighlight();
    };

    window.history.replaceState = function (...args) {
      originalReplaceState.apply(window.history, args);
      checkAndHighlight();
    };

    // Listen for popstate (back/forward navigation)
    window.addEventListener("popstate", checkAndHighlight);

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener("popstate", checkAndHighlight);
    };
  }, []);

  return <>{children}</>;
});

function searchAndHighlightText(pattern: RegExp): boolean {
  const walker = document.createTreeWalker(
    document.body,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode: (node) => {
        const parent = node.parentElement;

        // Skip script and style elements
        if (
          parent?.tagName === "SCRIPT" ||
          parent?.tagName === "STYLE" ||
          parent?.tagName === "NOSCRIPT"
        ) {
          return NodeFilter.FILTER_REJECT;
        }

        // Skip elements in slide-out panels (fixed/absolute positioned containers)
        let element: HTMLElement | null = parent;
        while (element && element !== document.body) {
          // Skip if element has data-exclude-text-highlight attribute
          if (element.hasAttribute?.("data-exclude-text-highlight")) {
            return NodeFilter.FILTER_REJECT;
          }

          const position = window.getComputedStyle(element).position;
          // Skip if in a fixed/absolute positioned container that's likely a modal/slideout
          if (
            (position === "fixed" || position === "absolute") &&
            element.offsetWidth > 200 // Likely a panel, not just a tooltip
          ) {
            return NodeFilter.FILTER_REJECT;
          }
          element = element.parentElement;
        }

        // Only accept nodes with matching text
        return node.textContent?.match(pattern)
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_SKIP;
      },
    },
  );

  let firstMatch: HTMLElement | null = null;
  const nodesToProcess: Text[] = [];

  // Collect all text nodes that match
  let node: Node | null;
  while ((node = walker.nextNode())) {
    nodesToProcess.push(node as Text);
  }

  // Process each text node
  for (const textNode of nodesToProcess) {
    const parent = textNode.parentNode;
    if (!parent) continue;

    const text = textNode.textContent || "";
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;

    // Reset regex state
    pattern.lastIndex = 0;
    let match;

    while ((match = pattern.exec(text)) !== null) {
      // Add text before match
      if (match.index > lastIndex) {
        fragment.append(
          document.createTextNode(text.slice(lastIndex, match.index)),
        );
      }

      // Add highlighted match
      const mark = document.createElement("mark");
      mark.style.backgroundColor = "#ffeb3b";
      mark.style.padding = "2px 4px";
      mark.style.borderRadius = "2px";
      mark.textContent = match[0];
      fragment.append(mark);

      // Store first match for scrolling
      if (!firstMatch) {
        firstMatch = mark;
      }

      lastIndex = pattern.lastIndex;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      fragment.append(document.createTextNode(text.slice(lastIndex)));
    }

    textNode.replaceWith(fragment);
  }

  // Scroll first match into view
  if (firstMatch) {
    firstMatch.scrollIntoView({ behavior: "smooth", block: "center" });
    return true;
  }

  return false;
}
